export const ZONES = ["head", "chest", "stomach", "waist", "legs"] as const;
export type Zone = (typeof ZONES)[number];
export type Alignment = "light" | "dark" | "neutral" | "none";
export type FightKind = "physical" | "group" | "chaotic";

export interface CombatStats {
  maxHp: number;
  strength: number;
  agility: number;
  intuition: number;
  endurance: number;
  armor: Partial<Record<Zone, number>>;
  armorActivationChance?: number;
  minDamage: number;
  maxDamage: number;
  crit: number;
  antiCrit: number;
  dodge: number;
  antiDodge: number;
}

export interface BattleMove {
  attack: Zone;
  blockStart: number;
}

export interface FighterSnapshot {
  id: string;
  name: string;
  avatar?: string;
  level: number;
  hp: number;
  isAi?: boolean;
  stats: CombatStats;
  consecutiveTimeouts: number;
  equipment?: FighterEquipment[];
}

export interface FighterEquipment {
  id: string;
  code: string;
  name: string;
  slot: string;
  durability: number;
  max_durability: number;
  required_level: number;
  price: number;
  min_damage: number;
  max_damage: number;
  armor: number;
  strength: number;
  agility: number;
  intuition: number;
  endurance: number;
  art_path?: string;
  requirements_json?: string;
  effects_json?: string;
  equipped_position?: number | null;
}

export interface ExchangeResult {
  attackerId: string;
  defenderId: string;
  attack: Zone;
  blocked: boolean;
  blockPenetrated: boolean;
  dodged: boolean;
  critical: boolean;
  damage: number;
  armorActivated: boolean;
  armorAbsorbed: number;
}

export interface RandomSource { next(): number }

export class SeededRandom implements RandomSource {
  private state: number;
  constructor(seed: number) { this.state = seed >>> 0 || 0x9e3779b9; }
  next(): number {
    let x = this.state;
    x ^= x << 13; x ^= x >>> 17; x ^= x << 5;
    this.state = x >>> 0;
    return this.state / 0x100000000;
  }
}

export function adjacentBlockZones(blockStart: number): readonly [Zone, Zone] {
  const normalized = Math.max(0, Math.min(ZONES.length - 1, Math.trunc(blockStart)));
  return [ZONES[normalized], ZONES[(normalized + 1) % ZONES.length]];
}

export function validateMove(move: BattleMove): boolean {
  return ZONES.includes(move.attack) && Number.isInteger(move.blockStart) && move.blockStart >= 0 && move.blockStart <= 4;
}

export function safeRandomMove(random: RandomSource): BattleMove {
  return {
    attack: ZONES[Math.floor(random.next() * ZONES.length)] ?? "chest",
    blockStart: Math.min(4, Math.floor(random.next() * 5))
  };
}

/**
 * BK1 never published the exact server coefficients. These constants reproduce
 * the documented BK1 relationships while keeping even a badly outmatched
 * fighter a small chance to land an exceptional hit.
 */
export const BK1_COMBAT_FORMULA = {
  baseCriticalChance: 0.045,
  criticalChanceSpan: 0.34,
  criticalModifierScale: 220,
  baseDodgeChance: 0.05,
  dodgeChanceSpan: 0.33,
  dodgeModifierScale: 160,
  minimumEventChance: 0.01,
  maximumCriticalChance: 0.50,
  maximumDodgeChance: 0.42,
  activeStatModifier: 5,
  specializationBonus: 0.45,
  maximumSpecializationModifier: 260,
  passiveStatModifier: 3,
  strengthDamage: 0.55,
  maximumArmorActivationChance: 0.70,
  failedArmorMitigation: 0.10,
  criticalArmorPenetration: 0.15,
  minimumArmorRoll: 0.35,
  maximumArmorRoll: 0.75
} as const;

export type CombatEvent = "critical" | "dodge";

export function opposedModifierChance(activeModifier: number, passiveModifier: number, event: CombatEvent = "critical"): number {
  const difference = activeModifier - passiveModifier;
  const chance = event === "dodge"
    ? BK1_COMBAT_FORMULA.baseDodgeChance + Math.tanh(difference / BK1_COMBAT_FORMULA.dodgeModifierScale) * BK1_COMBAT_FORMULA.dodgeChanceSpan
    : BK1_COMBAT_FORMULA.baseCriticalChance + Math.tanh(difference / BK1_COMBAT_FORMULA.criticalModifierScale) * BK1_COMBAT_FORMULA.criticalChanceSpan;
  const maximum = event === "dodge" ? BK1_COMBAT_FORMULA.maximumDodgeChance : BK1_COMBAT_FORMULA.maximumCriticalChance;
  return Math.max(BK1_COMBAT_FORMULA.minimumEventChance, Math.min(maximum, chance));
}

export function derivedCombatModifiers(
  agility: number,
  intuition: number,
  equipment: Partial<Pick<CombatStats, "crit" | "antiCrit" | "dodge" | "antiDodge">> = {}
): Pick<CombatStats, "crit" | "antiCrit" | "dodge" | "antiDodge"> {
  const activeFromStat = (stat: number, competingStat: number) => Math.round(
    stat * BK1_COMBAT_FORMULA.activeStatModifier
    + Math.min(BK1_COMBAT_FORMULA.maximumSpecializationModifier, Math.max(0, stat - competingStat) ** 2 * BK1_COMBAT_FORMULA.specializationBonus)
  );
  const passiveFromStat = (stat: number, competingStat: number) => Math.round(
    stat * BK1_COMBAT_FORMULA.passiveStatModifier * (stat > competingStat ? 1 : .5)
  );
  return {
    crit: activeFromStat(intuition, agility) + (equipment.crit ?? 0),
    // Ловкость помогает уйти от критического удара, а интуиция — прочитать
    // движение уворотчика. Так узкий специалист получает сильную сторону и
    // одновременно оставляет настоящую слабость, вместо четырёх бонусов сразу.
    antiCrit: passiveFromStat(agility, intuition) + (equipment.antiCrit ?? 0),
    dodge: activeFromStat(agility, intuition) + (equipment.dodge ?? 0),
    antiDodge: passiveFromStat(intuition, agility) + (equipment.antiDodge ?? 0)
  };
}

export function resolveStrike(
  attacker: FighterSnapshot,
  defender: FighterSnapshot,
  attackMove: BattleMove,
  defenseMove: BattleMove,
  random: RandomSource
): ExchangeResult {
  const blocked = adjacentBlockZones(defenseMove.blockStart).includes(attackMove.attack);
  // The displayed modifiers already include the relevant base stat and every
  // equipped item, so comparing them here avoids counting agility/intuition twice.
  const dodgeChance = opposedModifierChance(defender.stats.dodge, attacker.stats.antiDodge, "dodge");
  const dodged = !blocked && random.next() < dodgeChance;
  const critChance = opposedModifierChance(attacker.stats.crit, defender.stats.antiCrit);
  // A BK1 critical is checked even when the target zone is blocked: it can
  // penetrate that block for normal damage. Outside a block it deals double.
  const critical = !dodged && random.next() < critChance;
  const blockPenetrated = blocked && critical;
  const roll = attacker.stats.minDamage + Math.floor(random.next() * (attacker.stats.maxDamage - attacker.stats.minDamage + 1));
  const raw = roll + Math.floor(attacker.stats.strength * BK1_COMBAT_FORMULA.strengthDamage);
  const armor = defender.stats.armor[attackMove.attack] ?? 0;
  // Доспех закрывает большую часть зоны, но не является вечным плоским
  // вычитанием урона: оружие иногда находит щель между элементами защиты.
  const armorActivationChance = Math.min(
    BK1_COMBAT_FORMULA.maximumArmorActivationChance,
    Math.max(0, Number(defender.stats.armorActivationChance ?? 0) / 100)
  );
  const armorCoveredHit = armor > 0 && random.next() < armorActivationChance;
  const fullArmor = critical ? armor * (1 - BK1_COMBAT_FORMULA.criticalArmorPenetration) : armor;
  const armorRoll = armor <= 0
    ? 0
    : armorCoveredHit
      ? Math.round(fullArmor * (BK1_COMBAT_FORMULA.minimumArmorRoll + random.next() * (BK1_COMBAT_FORMULA.maximumArmorRoll - BK1_COMBAT_FORMULA.minimumArmorRoll)))
      : Math.round(armor * BK1_COMBAT_FORMULA.failedArmorMitigation);
  const multiplier = critical && !blocked ? 2 : 1;
  const damage = dodged || (blocked && !critical) ? 0 : Math.max(1, (raw - armorRoll) * multiplier);
  const unarmoredDamage = dodged || (blocked && !critical) ? 0 : raw * multiplier;
  const armorAbsorbed = armorCoveredHit ? Math.max(0, unarmoredDamage - damage) : 0;
  const armorActivated = armorCoveredHit && armorAbsorbed > 0;
  return { attackerId: attacker.id, defenderId: defender.id, attack: attackMove.attack, blocked, blockPenetrated, dodged, critical, damage, armorActivated, armorAbsorbed };
}

export function resolveExchange(
  first: FighterSnapshot,
  second: FighterSnapshot,
  firstMove: BattleMove,
  secondMove: BattleMove,
  random: RandomSource
): [ExchangeResult, ExchangeResult] {
  return [
    resolveStrike(first, second, firstMove, secondMove, random),
    resolveStrike(second, first, secondMove, firstMove, random)
  ];
}

export function experienceForWin(winnerLevel: number, loserLevel: number, kind: FightKind): number {
  const base = 12 + loserLevel * 8;
  const levelFactor = Math.max(0.25, Math.min(2, 1 + (loserLevel - winnerLevel) * 0.25));
  const kindFactor = kind === "physical" ? 1 : kind === "group" ? 1.15 : 1.25;
  return Math.max(1, Math.round(base * levelFactor * kindFactor * 1.5));
}

/** Victory experience with a personal contribution. Team fights must reward a
 * fighter who actually dealt damage instead of paying less than a short duel. */
export function battleExperienceForWin(
  winnerLevel: number,
  opponentLevels: readonly number[],
  kind: FightKind,
  damageDone: number,
  totalOpponentMaxHp: number
): number {
  const averageOpponentLevel = opponentLevels.length
    ? opponentLevels.reduce((sum, level) => sum + level, 0) / opponentLevels.length
    : winnerLevel;
  const base = experienceForWin(winnerLevel, Math.round(averageOpponentLevel), kind);
  const creditedDamage = Math.max(0, Math.min(damageDone, Math.max(1, totalOpponentMaxHp)));
  const damageValue = creditedDamage * (.08 + Math.max(0, averageOpponentLevel) * .015);
  const rosterBonus = 1 + Math.min(.25, Math.max(0, opponentLevels.length - 1) * .04);
  return Math.max(1, Math.round((base + damageValue) * rosterBonus));
}

export interface ProgressionStep {
  experience: number;
  level: number;
  freeStats: number;
  endurance: number;
  credits: number;
}

// На этом сервере кредитные награды за каждый ап вдвое выше канона БК1.
export const CREDIT_REWARD_MULTIPLIER = 2;

// Таблица развития раннего БК (0–7): кредиты и параметры выдаются на «апах»,
// а не произвольно после каждого боя. Значения сверены с энциклопедической
// таблицей OldBK; предел проекта намеренно остановлен на седьмом уровне.
export const PROGRESSION_STEPS: readonly ProgressionStep[] = [
  { experience: 20, level: 0, freeStats: 1, endurance: 0, credits: 1 },
  { experience: 45, level: 0, freeStats: 1, endurance: 0, credits: 2 },
  { experience: 75, level: 0, freeStats: 1, endurance: 0, credits: 3 },
  { experience: 110, level: 1, freeStats: 3, endurance: 1, credits: 4 },
  { experience: 160, level: 1, freeStats: 1, endurance: 0, credits: 5 },
  { experience: 215, level: 1, freeStats: 1, endurance: 0, credits: 6 },
  { experience: 280, level: 1, freeStats: 1, endurance: 0, credits: 7 },
  { experience: 350, level: 1, freeStats: 1, endurance: 0, credits: 8 },
  { experience: 410, level: 2, freeStats: 3, endurance: 1, credits: 9 },
  { experience: 530, level: 2, freeStats: 1, endurance: 0, credits: 10 },
  { experience: 670, level: 2, freeStats: 1, endurance: 0, credits: 11 },
  { experience: 830, level: 2, freeStats: 1, endurance: 0, credits: 12 },
  { experience: 950, level: 2, freeStats: 1, endurance: 0, credits: 13 },
  { experience: 1100, level: 2, freeStats: 1, endurance: 0, credits: 14 },
  { experience: 1300, level: 3, freeStats: 3, endurance: 1, credits: 15 },
  { experience: 1450, level: 3, freeStats: 1, endurance: 0, credits: 16 },
  { experience: 1650, level: 3, freeStats: 1, endurance: 0, credits: 17 },
  { experience: 1850, level: 3, freeStats: 1, endurance: 0, credits: 18 },
  { experience: 2050, level: 3, freeStats: 1, endurance: 0, credits: 19 },
  { experience: 2200, level: 3, freeStats: 1, endurance: 0, credits: 20 },
  { experience: 2500, level: 4, freeStats: 5, endurance: 1, credits: 21 },
  { experience: 2900, level: 4, freeStats: 1, endurance: 0, credits: 22 },
  { experience: 3350, level: 4, freeStats: 1, endurance: 0, credits: 23 },
  { experience: 3800, level: 4, freeStats: 1, endurance: 0, credits: 24 },
  { experience: 4200, level: 4, freeStats: 1, endurance: 0, credits: 25 },
  { experience: 4600, level: 4, freeStats: 1, endurance: 0, credits: 26 },
  { experience: 5000, level: 5, freeStats: 3, endurance: 1, credits: 27 },
  { experience: 6000, level: 5, freeStats: 1, endurance: 0, credits: 28 },
  { experience: 7000, level: 5, freeStats: 1, endurance: 0, credits: 29 },
  { experience: 8000, level: 5, freeStats: 1, endurance: 0, credits: 30 },
  { experience: 9000, level: 5, freeStats: 1, endurance: 0, credits: 31 },
  { experience: 10000, level: 5, freeStats: 1, endurance: 0, credits: 32 },
  { experience: 11000, level: 5, freeStats: 1, endurance: 0, credits: 33 },
  { experience: 12000, level: 5, freeStats: 1, endurance: 0, credits: 34 },
  { experience: 12500, level: 6, freeStats: 3, endurance: 1, credits: 35 },
  { experience: 14000, level: 6, freeStats: 1, endurance: 0, credits: 35 },
  { experience: 15500, level: 6, freeStats: 1, endurance: 0, credits: 35 },
  { experience: 17000, level: 6, freeStats: 1, endurance: 0, credits: 36 },
  { experience: 19000, level: 6, freeStats: 1, endurance: 0, credits: 36 },
  { experience: 21000, level: 6, freeStats: 1, endurance: 0, credits: 37 },
  { experience: 23000, level: 6, freeStats: 1, endurance: 0, credits: 37 },
  { experience: 27000, level: 6, freeStats: 1, endurance: 0, credits: 38 },
  { experience: 30000, level: 7, freeStats: 5, endurance: 1, credits: 41 }
] as const;

export function levelForExperience(experience: number): number {
  let level = 0;
  for (const step of PROGRESSION_STEPS) {
    if (step.experience > experience) break;
    level = Math.max(level, step.level);
  }
  return level;
}

export function progressionOutlook(experience: number, level = levelForExperience(experience)) {
  const safeExperience = Math.max(0, Math.trunc(experience));
  const nextAp = PROGRESSION_STEPS.find((step) => step.experience > safeExperience) ?? null;
  const nextLevel = PROGRESSION_STEPS.find((step) => step.experience > safeExperience && step.level > level) ?? null;
  return {
    nextApAt: nextAp?.experience ?? null,
    nextLevelAt: nextLevel?.experience ?? null,
    experienceToNextAp: nextAp ? nextAp.experience - safeExperience : null,
    experienceToNextLevel: nextLevel ? nextLevel.experience - safeExperience : null
  };
}

export function progressionBetween(previousExperience: number, nextExperience: number) {
  const crossed = PROGRESSION_STEPS.filter((step) => step.experience > previousExperience && step.experience <= nextExperience);
  return {
    crossed,
    level: levelForExperience(nextExperience),
    credits: crossed.reduce((sum, step) => sum + step.credits, 0) * CREDIT_REWARD_MULTIPLIER,
    freeStats: crossed.reduce((sum, step) => sum + step.freeStats, 0),
    endurance: crossed.reduce((sum, step) => sum + step.endurance, 0)
  };
}

export function injuryRoll(hpAfterFight: number, maxHp: number, random: RandomSource): "none" | "light" | "medium" | "heavy" {
  if (hpAfterFight > 0 || random.next() > 0.35) return "none";
  const roll = random.next();
  if (roll < 0.7) return "light";
  if (roll < 0.94 || maxHp < 70) return "medium";
  return "heavy";
}

export function canStartFight(level: number, kind: FightKind): boolean {
  // Внутри сервера учебные бои используют тот же безопасный движок physical.
  // В интерфейсе нулевого уровня они подписаны как «для новичков».
  return kind === "physical" ? level >= 0 : level >= 2;
}

export const HISTORIC_ITEMS = [
  { code: "armor_sorrow", name: "Тяжелая Броня Печали", slot: "armor", price: 145, level: 5, minDamage: 0, maxDamage: 0, armor: 18, durability: 100 },
  { code: "sword_beauty", name: "Меч Красоты", slot: "weapon", price: 85, level: 5, minDamage: 9, maxDamage: 16, armor: 0, durability: 50 },
  { code: "axe_woodcutter", name: "Топор Дровосека", slot: "weapon", price: 111, level: 6, minDamage: 11, maxDamage: 19, armor: 0, durability: 50 }
] as const;

export interface WireEvent<T = unknown> {
  type: string;
  eventId: string;
  serverTime: string;
  payload: T;
}

export function wireEvent<T>(type: string, payload: T): WireEvent<T> {
  return { type, eventId: crypto.randomUUID(), serverTime: new Date().toISOString(), payload };
}
